import Vue from 'vue';
import testDefaults from './test-defaults';
import CoreConfigType from './coreConfigType';
import _ from '../../utils';
import CaseInsensitiveRecord from '../../utils/caseInsensitiveRecord';

export interface ConfigType extends CoreConfigType {
  // Declared by app
}

export default class Config {
  private internalValues: ConfigType;

  isLoaded: boolean;

  promise: Promise<ConfigType> | undefined;

  readonly configFile: string;

  get values() { return this.internalValues; }

  set values(value) { this.internalValues = new CaseInsensitiveRecord(value) as any; }

  constructor() {
    const env = Vue.$version.buildConfiguration;
    switch (env) {
      case 'production':
        this.configFile = 'config.production.json';
        break;
      case 'staging':
        this.configFile = 'config.staging.json';
        break;
      default:
        this.configFile = 'config.development.json';
    }


    this.internalValues = {} as ConfigType;
    this.isLoaded = false;
  }

  /**
   * Retrieves the query name from the config key, this is the last portion of the key (delimited by hyphens).
   * @param configKey The config key to parse
   */
  getQueryNameFromConfigKey(configKey: keyof ConfigType): string {
    return configKey.toString().split('-').pop()!;
  }

  /**
   * Tries to get a value from the query string, then a route parameter value, or a config value (in that order).
   * The query key is case insensitive, so if there are query string parameters with the same name but different casing the last one will be used.
   * If no queryKey is provided it will be assumed to be the last portion of the configKey (delimited by hyphens).
   * @param configKey The key of the config value to retrieve
   * @param queryKey The key of the parameter or query value to retrieve, if this is not set the last portion of the configKey will be used
   * @returns The returned value will always be a string, if no value is found it will return undefined
   */
  getQueryOrValue(configKey: keyof ConfigType, queryKey?: string): string | undefined {
    queryKey = queryKey?.toLowerCase() ?? this.getQueryNameFromConfigKey(configKey);
    const queries = Vue.$routerUtils.getQueryParams();
    const queriesLowerCase = {} as any;
    Object.keys(queries).forEach((queryKeyName) => {
      queriesLowerCase[queryKeyName.toLowerCase()] = queries[queryKeyName];
    });
    const value = queriesLowerCase[queryKey] ?? Vue.$router.currentRoute.params[queryKey] ?? this.values[configKey]?.toString();
    return value;
  }

  /**
   * Gets a specific value for the requested language, all supplied tokens are replaced as well (if possible).
   * If no language is supplied it defaults to the current user language.
   * Tokens that are provided by default:
   * ```{{language}}```
   */
  getValueTranslated(configKey: keyof ConfigType, tokens: Record<string, string>, language?: string): ConfigType[keyof ConfigType] | undefined {
    const languageCode = language ?? Vue.$translation.get().toLowerCase();
    let configValue = (this.values as any)[`${configKey}-${languageCode}`] as (string | undefined) ?? this.values[configKey];

    if (Vue._.isNil(configValue)) {
      return undefined;
    }

    if (typeof configValue !== 'string') {
      return configValue;
    }

    Object.keys(tokens).forEach((key) => {
      configValue = (configValue as string).replace(new RegExp(`\{${key}\}`), tokens[key]);
    });
    configValue = configValue.replace(/\{language\}/, languageCode);

    return configValue;
  }

  refresh(): Promise<ConfigType> {
    if (Vue.$version.buildConfiguration === 'test') {
      this.values = testDefaults as ConfigType;
      return Promise.resolve(testDefaults as any);
    }

    const base = `${Vue.$appConfigPath}/`;
    const configUrl = base + this.configFile;
    this.isLoaded = false;
    this.promise = new Promise((resolve) => {
      Vue.$fetch.get(configUrl, { baseURL: '' })
        .then((result) => {
          if (typeof (result.data) !== 'string') {
            this.values = result.data;
            Vue.$log.info(`[CONFIG] 📝 read config at: ${configUrl}`);
            Vue.$log.info(this.values);
          } else {
            Vue.$log.error('Unable to load config.json from server, invalid JSON, using default values instead');
            try {
            // This will fail
              JSON.parse(result.data);
            } catch (e) {
            // Otherwise there is no information what's wrong with the JSON
              Vue.$log.error(e);
              Vue.$log.error(`Expected valid JSON, but received this: ${result.data}`);
            }
            // configValues = defaultConfig;
          }
        })
        .catch((error) => {
          Vue.$log.error(`Error in request, unable to load config.json from server, 
                         using default values instead, error: ${error?.statusText ?? error}`);
          // this.cache = defaultConfig;
          this.isLoaded = true;
        })
        .finally(() => {
          let baseUrl = this.values['networking-api-base-url'];
          if (!_.isEmpty(baseUrl)) {
            if (!baseUrl.endsWith('/')) {
              baseUrl += '/';
            }
            let apiPath = this.values['networking-api-path'] ?? '';
            if (!apiPath.endsWith('/')) {
              apiPath += '/';
            }
            const fullBaseUrl = baseUrl + apiPath;
            Vue.$fetch.defaults.baseUrl = fullBaseUrl;
          }
          this.isLoaded = true;
          resolve(this.values);
        });
    });
    return this.promise;
  }
}
